import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable } from '@angular/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { SnackBarComponent } from './components/snack-bar/snack-bar.component';
import { MessagePosition } from './interfaces/message-position.enum';
import { MessageType } from './interfaces/message-type.enum';

const snackBarOffsetPosition = '1rem';

@Injectable()
export class SnackBarService {
  private openSubject = new Subject<void>();
  private closeSubject = new Subject<void>();
  private triggerActionSubject = new Subject<void>();

  /** SnackBarComponent instance */
  private snackBarInstance: null | SnackBarComponent;
  /** OverlayRef instance */
  private overlayRef: OverlayRef;
  /** ComponentPortal instance */
  private portal: ComponentPortal<SnackBarComponent>;

  get opened() {
    return this.openSubject.asObservable();
  }

  get closed() {
    return this.closeSubject.asObservable();
  }

  get triggeredAction() {
    return this.triggerActionSubject.asObservable();
  }

  /** Dependencies */
  constructor(private overlay: Overlay) {}

  public show(
    message: string,
    position = MessagePosition.BOTTOM,
    type = MessageType.INFO,
    actionName?: string,
    hideDelay = 6000,
  ) {
    this.detach();

    this.createOverlay(position);
    this.portal = this.portal || new ComponentPortal(SnackBarComponent);

    this.snackBarInstance = this.overlayRef.attach(this.portal).instance;
    if (type) {
      this.snackBarInstance.type = type;
    }
    this.snackBarInstance.message = message;
    this.snackBarInstance.actionName = actionName ?? '';

    this.openSubject.next();
    this.setCloseTimer(hideDelay);
    this.setTriggeredActionListener();
  }

  private createOverlay(position: MessagePosition) {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      [position](snackBarOffsetPosition);

    this.overlayRef = this.overlay.create({
      hasBackdrop: false,
      positionStrategy,
    });
  }

  private setTriggeredActionListener() {
    if (!this.snackBarInstance) {
      return;
    }
    this.snackBarInstance.triggeredAction
      .pipe(takeUntil(this.opened), takeUntil(this.closed))
      .subscribe(() => {
        this.triggerAction();
      });
  }

  private setCloseTimer(hideDelay: number) {
    timer(hideDelay)
      .pipe(takeUntil(this.opened), takeUntil(this.closed))
      .subscribe(() => this.close());
  }

  public close() {
    this.closeSubject.next();
    this.detach();
  }

  public triggerAction() {
    this.triggerActionSubject.next();
    this.close();
  }

  /**
   * Detach OverlayRef from portal
   */
  private detach() {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }

    this.snackBarInstance = null;
  }
}
